import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Check, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshSubscription } = useAuth();
  const [status, setStatus] = useState('checking');
  const [tier, setTier] = useState('');
  const polledRef = useRef(false);
  const sessionId = searchParams.get('session_id');

  const pollCheckout = useCallback(async (id, tkn, attempt = 0) => {
    if (attempt >= 6) {
      setStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${API}/subscription/checkout/status/${id}`, {
        headers: { Authorization: `Bearer ${tkn}` }
      });
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        setTier(res.data.tier);
        refreshSubscription();
        return;
      }
    } catch (_err) { /* Polling retry — failure expected during processing */ }
    setTimeout(() => pollCheckout(id, tkn, attempt + 1), 2000);
  }, [refreshSubscription]);

  useEffect(() => {
    if (!sessionId || !token || polledRef.current) return;
    polledRef.current = true;
    pollCheckout(sessionId, token);
  }, [sessionId, token, pollCheckout]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 text-center border border-[#EBE7E0] shadow-xl" data-testid="subscription-success-card">
        {status === 'checking' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-[#D4AF37] mx-auto mb-4" />
            <h2 className="font-serif-display text-xl text-[#14213D] mb-2">Processing Payment&hellip;</h2>
            <p className="text-sm text-[#5A6577]">Confirming your subscription with Stripe.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-[#FBF6E8] border border-[#D4AF37]/40 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-[#D4AF37]" />
            </div>
            <h2 className="font-serif-display text-xl text-[#14213D] mb-2">Subscription Active!</h2>
            <p className="text-sm text-[#5A6577] mb-6">
              Your <strong className="capitalize">{tier}</strong> plan is now active. Welcome to the full Atlas History library.
            </p>
            <Button className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]" onClick={() => navigate('/')} data-testid="go-home-btn">
              Start Exploring
            </Button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <AlertTriangle className="h-12 w-12 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="font-serif-display text-xl text-[#14213D] mb-2">Payment Processing</h2>
            <p className="text-sm text-[#5A6577] mb-6">
              Your payment is still being processed. It may take a moment. Please check back shortly.
            </p>
            <Button variant="outline" onClick={() => navigate('/subscription')} data-testid="check-again-btn">
              Check Subscription Status
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
